import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import type { LatLngTuple } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getCountryConfig, DEFAULT_COUNTRY } from '../../data/countryMaps';
import { MAP_RATING_COLORS } from '../../constants/colors';
import { useInstitution } from '../../context/InstitutionContext';
import { useRatedInstitutionsWithRatings } from '../../hooks/useInstitutions';
import ProvinceDetail from './components/ProvinceDetail';
import RatingLegend, { legendItems } from './components/RatingLegend';
import SearchInput from './components/SearchInput';
import StatsCards from './components/StatsCards';
import TopRatedInstitutions from './components/TopRatedInstitutions';
import RecentRatingChanges from './components/RecentRatingChanges';
import type { RatingChange } from './components/RecentRatingChanges';
import './RatingsMap.css';

// Feature flag: Set to 'false' to use static JSON data instead of BigQuery API
const USE_DYNAMIC_DATA = import.meta.env.VITE_DYNAMIC_INSTITUTIONS !== 'false';

// Legacy imports for fallback mode (only used when USE_DYNAMIC_DATA is false)
import { institutionLocations } from '../../data/institutionLocations';
import realRatingData from '../../data/realRatingData.json';

interface ProvinceProperties {
  province: string;
  country: string;
}

interface ProvinceRating {
  province: string;
  avgScore: number;
  letterGrade: string;
  institutionCount: number;
  institutions: {
    id: string;
    name: string;
    shortName: string;
    city: string;
    score: number;
    rating: string;
  }[];
}

// Rating thresholds (based on BigQuery data)
const getRatingCategory = (score: number): string => {
  if (score >= 89) return 'AAA';
  if (score >= 78) return 'AA';
  if (score >= 68) return 'A';
  if (score >= 57) return 'BBB';
  if (score >= 48) return 'BB';
  return 'B';
};

const MAP_BORDER_COLOR = '#666';
const MAP_SELECTED_BORDER = '#1976d2';

const getRatingColor = (score: number | null): string => {
  if (score === null) return MAP_RATING_COLORS['N/A'];
  return MAP_RATING_COLORS[getRatingCategory(score)] ?? MAP_RATING_COLORS['N/A'];
};

// The two periods for year-over-year comparison
const LATEST_PERIOD = '2025-05';
const PREVIOUS_PERIOD = '2024-05';

const formatPeriodLabel = (period: string): string => {
  const [year, month] = period.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

// Helper to create short name from institution name
const makeShortName = (name: string): string => {
  return name
    .replace(/^COOPERATIVA DE AHORRO Y CREDITO\s*/i, '')
    .replace(/^ASOCIACION MUTUALISTA DE AHORRO Y CREDITO PARA LA VIVIENDA\s*/i, 'Mutualista ')
    .replace(/^CAJA CENTRAL\s*/i, '')
    .replace(/\s+LTDA\.?$/i, '')
    .replace(/\s+LIMITADA$/i, '')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

interface RatingsMapProps {
  countryCode?: string;
}

interface GeoJSONLayerProps {
  data: GeoJSON.FeatureCollection<GeoJSON.Geometry, ProvinceProperties>;
  provinceRatings: Record<string, ProvinceRating>;
  selectedProvince: string | null;
  onProvinceClick: (province: string | null) => void;
}

const GeoJSONLayer: React.FC<GeoJSONLayerProps> = ({
  data,
  provinceRatings,
  selectedProvince,
  onProvinceClick,
}) => {
  const map = useMap();

  useEffect(() => {
    const getStyle = (feature?: GeoJSON.Feature) => {
      if (!feature?.properties) {
        return {
          fillColor: MAP_RATING_COLORS['N/A'],
          weight: 1,
          opacity: 1,
          color: MAP_BORDER_COLOR,
          fillOpacity: 0.7,
        };
      }

      const provinceName = (feature.properties as ProvinceProperties).province;
      const rating = provinceRatings[provinceName];
      const score = rating ? rating.avgScore : null;
      const isSelected = selectedProvince === provinceName;

      return {
        fillColor: getRatingColor(score),
        weight: isSelected ? 3 : 1,
        opacity: 1,
        color: isSelected ? MAP_SELECTED_BORDER : MAP_BORDER_COLOR,
        fillOpacity: isSelected ? 0.9 : 0.7,
      };
    };

    const geoJsonLayer = L.geoJSON(data, {
      style: getStyle,
      onEachFeature: (feature, layer) => {
        const provinceName = (feature.properties as ProvinceProperties)?.province;
        const rating = provinceRatings[provinceName];

        let tooltipContent = `<strong>${provinceName}</strong>`;
        if (rating) {
          tooltipContent += `<br/>${i18n.t('map.avgScoreLabel')} ${rating.avgScore.toFixed(1)}`;
          tooltipContent += `<br/>${rating.institutionCount} ${rating.institutionCount !== 1 ? i18n.t('common.institutions') : i18n.t('common.institution')}`;
        } else {
          tooltipContent += `<br/><em>${i18n.t('map.noRatedInstitutions')}</em>`;
        }

        layer.bindTooltip(tooltipContent, {
          sticky: true,
          className: 'province-tooltip',
        });

        layer.on({
          click: () => {
            onProvinceClick(selectedProvince === provinceName ? null : provinceName);
          },
          mouseover: (e) => {
            const target = e.target as L.Path;
            target.setStyle({ weight: 2, fillOpacity: 0.85 });
          },
          mouseout: (e) => {
            const target = e.target as L.Path;
            const isSelected = selectedProvince === provinceName;
            target.setStyle({
              weight: isSelected ? 3 : 1,
              fillOpacity: isSelected ? 0.9 : 0.7,
            });
          },
        });
      },
    });

    geoJsonLayer.addTo(map);
    return () => { map.removeLayer(geoJsonLayer); };
  }, [map, data, provinceRatings, selectedProvince, onProvinceClick]);

  return null;
};

// Legacy types for static data fallback
interface RatingDataEntry {
  institution_name: string;
  rating: string;
  date: string;
  finalScore: number;
}

const RatingsMap: React.FC<RatingsMapProps> = ({ countryCode = DEFAULT_COUNTRY }) => {
  const { t } = useTranslation();
  const [selectedProvince, setSelectedProvince] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [expandedGrade, setExpandedGrade] = useState<string | null>(null);
  const [geoJsonData, setGeoJsonData] = useState<GeoJSON.FeatureCollection<GeoJSON.Geometry, ProvinceProperties> | null>(null);
  const { setSelectedInstitution } = useInstitution();

  // Fetch two periods for comparison
  const periodsForApi = useMemo(() => {
    if (!USE_DYNAMIC_DATA) return undefined;
    return [LATEST_PERIOD, PREVIOUS_PERIOD];
  }, []);

  const { institutions: dynamicInstitutions, isLoading, error } = useRatedInstitutionsWithRatings(periodsForApi);

  // Separate institutions by period
  const { currentInstitutions, previousInstitutions } = useMemo(() => {
    if (!USE_DYNAMIC_DATA) return { currentInstitutions: dynamicInstitutions, previousInstitutions: [] };
    const current = dynamicInstitutions.filter(inst => inst.period === LATEST_PERIOD);
    const previous = dynamicInstitutions.filter(inst => inst.period === PREVIOUS_PERIOD);
    return { currentInstitutions: current, previousInstitutions: previous };
  }, [dynamicInstitutions]);

  const countryConfig = useMemo(() => getCountryConfig(countryCode), [countryCode]);
  const mapCenter: LatLngTuple = countryConfig.center;

  // Load GeoJSON
  useEffect(() => {
    fetch(countryConfig.geoJsonPath)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to fetch: ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (!data || !data.features || !Array.isArray(data.features)) {
          throw new Error('Invalid GeoJSON structure');
        }
        const validFeatures = data.features.filter((f: GeoJSON.Feature) => {
          const hasGeometry = f.geometry && 'coordinates' in f.geometry;
          const hasProperties = f.properties && f.properties.province;
          return hasGeometry && hasProperties;
        });
        setGeoJsonData({
          ...data,
          features: validFeatures,
        } as GeoJSON.FeatureCollection<GeoJSON.Geometry, ProvinceProperties>);
      })
      .catch((err) => console.error('Failed to load GeoJSON:', err));
  }, [countryConfig.geoJsonPath]);

  // Province ratings from current period
  const provinceRatings = useMemo(() => {
    const ratings: Record<string, ProvinceRating> = {};
    const countryMap: Record<string, string> = { 'Ecuador': 'EC', 'Colombia': 'CO', 'Peru': 'PE' };

    if (USE_DYNAMIC_DATA) {
      currentInstitutions.forEach((inst) => {
        const instCountryCode = countryMap[inst.country] || inst.country;
        if (instCountryCode !== countryCode) return;

        if (!ratings[inst.province]) {
          ratings[inst.province] = {
            province: inst.province, avgScore: 0, letterGrade: '',
            institutionCount: 0, institutions: [],
          };
        }

        ratings[inst.province].institutions.push({
          id: inst.institution_id,
          name: inst.institution_name,
          shortName: makeShortName(inst.institution_name),
          city: inst.city,
          score: inst.numeric_score,
          rating: inst.rating_category,
        });
      });
    } else {
      Object.entries(institutionLocations).forEach(([institutionId, location]) => {
        if (location.country !== countryCode) return;
        const ratingData = (realRatingData as Record<string, RatingDataEntry>)[institutionId];
        if (!ratingData) return;

        if (!ratings[location.province]) {
          ratings[location.province] = {
            province: location.province, avgScore: 0, letterGrade: '',
            institutionCount: 0, institutions: [],
          };
        }

        ratings[location.province].institutions.push({
          id: institutionId,
          name: ratingData.institution_name,
          shortName: location.shortName,
          city: location.city,
          score: ratingData.finalScore,
          rating: ratingData.rating,
        });
      });
    }

    Object.values(ratings).forEach((provinceData) => {
      const totalScore = provinceData.institutions.reduce((sum, inst) => sum + inst.score, 0);
      provinceData.avgScore = provinceData.institutions.length > 0
        ? totalScore / provinceData.institutions.length : 0;
      provinceData.letterGrade = getRatingCategory(provinceData.avgScore);
      provinceData.institutionCount = provinceData.institutions.length;
    });

    return ratings;
  }, [countryCode, currentInstitutions, dynamicInstitutions]);

  // Aggregate stats
  const { totalInstitutions, averageRating, allInstitutionsFlat } = useMemo(() => {
    const allInst: Array<{
      id: string; name: string; shortName: string; city: string;
      province: string; score: number; rating: string;
    }> = [];

    Object.entries(provinceRatings).forEach(([province, data]) => {
      data.institutions.forEach((inst) => {
        allInst.push({ ...inst, province });
      });
    });

    const total = allInst.length;
    const avgScore = total > 0
      ? allInst.reduce((sum, inst) => sum + inst.score, 0) / total : 0;

    return {
      totalInstitutions: total,
      averageRating: getRatingCategory(avgScore),
      allInstitutionsFlat: allInst,
    };
  }, [provinceRatings]);

  // Upgraded/downgraded and rating changes
  const { upgradedCount, downgradedCount, ratingChanges } = useMemo(() => {
    // Static data fallback: generate sample changes from existing institutions
    if (!USE_DYNAMIC_DATA || previousInstitutions.length === 0) {
      const ratingOrder = ['B', 'BB', 'BBB', 'A', 'AA', 'AAA'];
      const sampleChanges: RatingChange[] = [];
      const allInst = Object.values(provinceRatings).flatMap(p => p.institutions);
      // Pick a few institutions and simulate rating changes
      const candidates = allInst.filter(i => i.score > 0).sort((a, b) => b.score - a.score);
      const samplePairs: { idx: number; dir: 'upgraded' | 'downgraded'; period: string }[] = [
        { idx: 0, dir: 'upgraded', period: LATEST_PERIOD },
        { idx: 5, dir: 'upgraded', period: LATEST_PERIOD },
        { idx: 10, dir: 'downgraded', period: LATEST_PERIOD },
        { idx: 15, dir: 'downgraded', period: LATEST_PERIOD },
        { idx: 20, dir: 'upgraded', period: LATEST_PERIOD },
      ];
      let up = 0;
      let down = 0;
      samplePairs.forEach(({ idx, dir, period }) => {
        const inst = candidates[idx];
        if (!inst) return;
        const currRating = getRatingCategory(inst.score);
        const currIdx = ratingOrder.indexOf(currRating);
        const prevRating = dir === 'upgraded'
          ? ratingOrder[Math.max(0, currIdx - 1)]
          : ratingOrder[Math.min(ratingOrder.length - 1, currIdx + 1)];
        if (prevRating === currRating) return;
        if (dir === 'upgraded') up++;
        else down++;
        sampleChanges.push({
          id: inst.name,
          name: inst.name,
          shortName: makeShortName(inst.name),
          date: period,
          oldRating: dir === 'upgraded' ? prevRating : prevRating,
          newRating: currRating,
          direction: dir,
        });
      });
      return { upgradedCount: up, downgradedCount: down, ratingChanges: sampleChanges };
    }

    const countryMap: Record<string, string> = { 'Ecuador': 'EC', 'Colombia': 'CO', 'Peru': 'PE' };
    const ratingOrder = ['B', 'BB', 'BBB', 'A', 'AA', 'AAA'];

    const prevMap = new Map<string, { rating: string; name: string }>();
    previousInstitutions.forEach((inst) => {
      const instCountryCode = countryMap[inst.country] || inst.country;
      if (instCountryCode === countryCode) {
        prevMap.set(inst.institution_id, { rating: inst.rating_category, name: inst.institution_name });
      }
    });

    let upgraded = 0;
    let downgraded = 0;
    const changes: RatingChange[] = [];

    currentInstitutions.forEach((inst) => {
      const instCountryCode = countryMap[inst.country] || inst.country;
      if (instCountryCode !== countryCode) return;

      const prev = prevMap.get(inst.institution_id);
      if (!prev) return;

      const prevIdx = ratingOrder.indexOf(prev.rating);
      const currIdx = ratingOrder.indexOf(inst.rating_category);

      if (currIdx !== prevIdx) {
        const direction = currIdx > prevIdx ? 'upgraded' : 'downgraded';
        if (direction === 'upgraded') upgraded++;
        else downgraded++;

        changes.push({
          id: inst.institution_id,
          name: inst.institution_name,
          shortName: makeShortName(inst.institution_name),
          date: LATEST_PERIOD,
          oldRating: prev.rating,
          newRating: inst.rating_category,
          direction,
        });
      }
    });

    changes.sort((a, b) => (a.direction === 'upgraded' ? -1 : 1) - (b.direction === 'upgraded' ? -1 : 1));

    return { upgradedCount: upgraded, downgradedCount: downgraded, ratingChanges: changes };
  }, [currentInstitutions, previousInstitutions, countryCode, provinceRatings]);

  // Top 5 by score
  const topRatedInstitutions = useMemo(() => {
    return [...allInstitutionsFlat].sort((a, b) => b.score - a.score).slice(0, 5);
  }, [allInstitutionsFlat]);

  // Rating distribution with institution lists for expand
  const gradeGroups = useMemo(() => {
    const groups: Record<string, {
      grade: string; color: string; count: number;
      institutions: typeof allInstitutionsFlat;
    }> = {};
    legendItems.forEach((item) => {
      if (item.label !== 'N/A') {
        groups[item.label] = { grade: item.label, color: item.color, count: 0, institutions: [] };
      }
    });
    allInstitutionsFlat.forEach((inst) => {
      const grade = getRatingCategory(inst.score);
      if (groups[grade]) {
        groups[grade].count++;
        groups[grade].institutions.push(inst);
      }
    });
    // Sort institutions within each group by score descending
    Object.values(groups).forEach((group) => {
      group.institutions.sort((a, b) => b.score - a.score);
    });
    return groups;
  }, [allInstitutionsFlat]);

  const handleGradeClick = (grade: string) => {
    setExpandedGrade(expandedGrade === grade ? null : grade);
  };

  // Search results
  const filteredInstitutions = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase().trim();
    return allInstitutionsFlat
      .filter((inst) =>
        inst.name.toLowerCase().includes(query) ||
        inst.shortName.toLowerCase().includes(query) ||
        inst.city.toLowerCase().includes(query)
      )
      .sort((a, b) => b.score - a.score);
  }, [allInstitutionsFlat, searchQuery]);

  const handleInstitutionClick = (inst: { id: string; name: string }) => {
    setSelectedInstitution({ id: inst.id, name: inst.name });
  };

  const selectedProvinceData = selectedProvince ? provinceRatings[selectedProvince] : null;

  if (USE_DYNAMIC_DATA && isLoading) {
    return (
      <div className="ratings-map">
        <div className="heatmap-container">
          <div className="detail-panel" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <p>{t('map.loadingInstitutions')}</p>
          </div>
          <div className="map-wrapper" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f0f0' }}>
            <p>{t('map.loadingMap')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (USE_DYNAMIC_DATA && error) {
    return (
      <div className="ratings-map">
        <div className="heatmap-container">
          <div className="detail-panel" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d32f2f' }}>
            <p>{t('map.errorLoading', { error })}</p>
          </div>
          <div className="map-wrapper" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f0f0' }}>
            <p>{t('map.refreshPage')}</p>
          </div>
        </div>
      </div>
    );
  }

  const maxDistCount = Math.max(...Object.values(gradeGroups).map(g => g.count), 1);

  return (
    <div className="ratings-map">
      {/* Page Header */}
      <div className="ratings-page-header">
        <div className="ratings-page-header-text">
          <h1 className="ratings-page-title">{t('nav.ratings')}</h1>
          <p className="ratings-page-subtitle">{t('map.clickProvince')}</p>
        </div>
        <span className="last-updated-badge">
          {t('map.lastUpdated', { date: formatPeriodLabel(LATEST_PERIOD) })}
        </span>
      </div>

      {/* Two-column layout: left panel + map */}
      <div className="heatmap-container">
        <div className="detail-panel">
          {/* Search inside panel */}
          <div className="heatmap-search-wrapper">
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder={t('map.searchPlaceholder')}
            />
          </div>

          {searchQuery.trim() ? (
            /* Search Results */
            <div className="panel-scroll-area">
              <p className="search-results-count">
                {t('map.institutionsFound', { count: filteredInstitutions.length })}
              </p>
              {filteredInstitutions.length === 0 ? (
                <p className="no-results">{t('map.noResults')}</p>
              ) : (
                <ul className="institution-results">
                  {filteredInstitutions.map((inst) => (
                    <li
                      key={inst.id}
                      className="institution-result"
                      onClick={() => handleInstitutionClick(inst)}
                    >
                      <div className="inst-result-info">
                        <span className="inst-result-name">{inst.shortName}</span>
                        <span className="inst-result-location">
                          {inst.city}, {inst.province}
                        </span>
                      </div>
                      <span
                        className="province-rating"
                        style={{ backgroundColor: getRatingColor(inst.score) }}
                      >
                        {getRatingCategory(inst.score)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : selectedProvinceData ? (
            <ProvinceDetail
              province={selectedProvince!}
              data={selectedProvinceData}
              onClose={() => setSelectedProvince(null)}
            />
          ) : (
            /* Dashboard sections */
            <div className="panel-scroll-area">
              {/* Statistics Cards */}
              <StatsCards
                totalInstitutions={totalInstitutions}
                averageRating={averageRating}
                upgradedCount={upgradedCount}
                downgradedCount={downgradedCount}
                comparisonPeriod={formatPeriodLabel(PREVIOUS_PERIOD)}
              />

              <div className="dashboard-sections">
                {/* Rating Distribution */}
                <div className="rating-distribution-card">
                  <h3 className="section-title">{t('map.ratingDistribution')}</h3>
                  <div className="rating-distribution">
                    {legendItems
                      .filter((item) => item.label !== 'N/A')
                      .map((item) => {
                        const group = gradeGroups[item.label];
                        const count = group?.count || 0;
                        const barWidth = maxDistCount > 0 ? (count / maxDistCount) * 100 : 0;
                        const isExpanded = expandedGrade === item.label;
                        return (
                          <div key={item.label} className="distribution-row">
                            <div
                              className={`distribution-bar-container ${isExpanded ? 'expanded' : ''}`}
                              onClick={() => count > 0 && handleGradeClick(item.label)}
                              style={{ cursor: count > 0 ? 'pointer' : 'default' }}
                            >
                              <span className="distribution-label">{item.label}</span>
                              <div className="distribution-bar-wrapper">
                                <div
                                  className="distribution-bar"
                                  style={{
                                    width: `${barWidth}%`,
                                    backgroundColor: item.color,
                                  }}
                                />
                              </div>
                              <span className="distribution-count">
                                {count} {t('map.institutionsSuffix')}
                              </span>
                            </div>
                            {isExpanded && count > 0 && (
                              <ul className="grade-institutions">
                                {group.institutions.map((inst) => (
                                  <li
                                    key={inst.id}
                                    className="institution-item"
                                    onClick={() => handleInstitutionClick(inst)}
                                  >
                                    <div className="inst-info">
                                      <span className="inst-name">{inst.shortName}</span>
                                      <span className="inst-location">
                                        {inst.city}, {inst.province}
                                      </span>
                                    </div>
                                    <span className="inst-score">{inst.score.toFixed(1)}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>

                {/* Top Rated Institutions */}
                <TopRatedInstitutions
                  institutions={topRatedInstitutions}
                  onInstitutionClick={handleInstitutionClick}
                />

                {/* Recent Rating Changes */}
                <RecentRatingChanges
                  changes={ratingChanges}
                  onInstitutionClick={handleInstitutionClick}
                  comparisonLabel={`${formatPeriodLabel(PREVIOUS_PERIOD)} → ${formatPeriodLabel(LATEST_PERIOD)}`}
                />
              </div>
            </div>
          )}
        </div>

        <div className="map-wrapper">
          <MapContainer
            center={mapCenter}
            zoom={countryConfig.zoom}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
            zoomControl={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://carto.com/">CARTO</a>'
              url="https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png"
            />
            {geoJsonData && (
              <GeoJSONLayer
                data={geoJsonData}
                provinceRatings={provinceRatings}
                selectedProvince={selectedProvince}
                onProvinceClick={setSelectedProvince}
              />
            )}
          </MapContainer>
          <RatingLegend />
        </div>
      </div>
    </div>
  );
};

export default RatingsMap;
